import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@paulownia/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@paulownia/shared/components/ui/card';
import { Switch } from '@paulownia/shared/components/ui/switch';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Textarea } from '@paulownia/shared/components/ui/textarea';
import { ArrowLeft, BarChart3, Bell, BellOff, Calendar, AlertCircle, Camera, GripVertical, QrCode, Save, Store, Timer, Clock, X, Phone, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant, useHasRole, useIsSuperAdmin } from '@paulownia/shared/lib/tenant-context';
import { getTenantById, updateTenantSettings, updateTenantIsActive, updateTenantAbsence, updateTenantProfile } from '@paulownia/shared/services/tenant';
import { uploadMerchantProfilePhoto, deleteMerchantProfilePhoto, isValidImageFormat } from '@paulownia/shared/services/images';
import { isTenantAbsent, DAY_OF_WEEK_LABELS } from '@paulownia/shared/types';
import type { Market, TenantMarket } from '@paulownia/shared/types';
import { listProducts } from '@paulownia/shared/services/products';
import { getOrderSourceStats, type OrderSourceStats } from '@paulownia/shared/services/orders';
import { getTenantMarketsWithDetails, updateTenantMarket } from '@paulownia/shared/services/markets';
import {
  requestNotificationPermissionWithDetails,
  hasNotificationPermission,
  isNotificationPermissionDenied,
  setupForegroundMessageListener,
} from '@paulownia/shared/services/notifications';
import {
  saveMerchantNotificationToken,
  removeMerchantNotificationToken,
  getMerchantNotificationToken,
} from '@paulownia/shared/services/merchant-notifications';
import { cn } from '@paulownia/shared/lib/utils';
import { QRCodeSection } from '../components/QRCodeSection';

// Helper to capitalize first letter
function capitalize(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Sortable Section Item Component
function SortableSectionItem({
  section,
  index,
}: {
  section: string;
  index: number;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-3 bg-white border rounded-lg p-4 transition-all',
        'hover:shadow-md hover:border-gray-300',
        isDragging && 'shadow-lg border-blue-300 bg-blue-50/50 z-50'
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className={cn(
          'flex-shrink-0 cursor-grab active:cursor-grabbing p-1 rounded touch-none',
          'opacity-40 hover:opacity-100 transition-opacity',
          'hover:bg-gray-100',
          isDragging && 'opacity-100'
        )}
      >
        <GripVertical className="h-5 w-5 text-gray-400" />
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-900">{capitalize(section)}</h3>
        <p className="text-sm text-gray-500">Position: {index + 1}</p>
      </div>
    </div>
  );
}

// Sortable SubSection Item Component
function SortableSubSectionItem({
  subSection,
  index,
}: {
  subSection: string;
  index: number;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: subSection });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 bg-gray-50 border rounded-md p-2 transition-all',
        'hover:shadow-sm hover:border-gray-300',
        isDragging && 'shadow-md border-blue-300 bg-blue-50/50 z-50'
      )}
    >
      <div
        {...attributes}
        {...listeners}
        className={cn(
          'flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 rounded touch-none',
          'opacity-40 hover:opacity-100 transition-opacity',
          'hover:bg-gray-200',
          isDragging && 'opacity-100'
        )}
      >
        <GripVertical className="h-4 w-4 text-gray-400" />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-700">{capitalize(subSection)}</span>
        <span className="text-xs text-gray-400 ml-2">#{index + 1}</span>
      </div>
    </div>
  );
}

export const TenantSettings = () => {
  const navigate = useNavigate();
  const { currentTenantId, user } = useTenant();
  const isSuperAdmin = useIsSuperAdmin();
  const isOwner = useHasRole('owner');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [savingAbsence, setSavingAbsence] = useState(false);
  const [sections, setSections] = useState<string[]>([]);
  const [subSectionsBySection, setSubSectionsBySection] = useState<Record<string, string[]>>({});
  const [isActive, setIsActive] = useState(true);
  const [absenceStart, setAbsenceStart] = useState('');
  const [absenceEnd, setAbsenceEnd] = useState('');
  const [, setTenantSettings] = useState<{
    sectionOrder?: Record<string, number>;
    subSectionOrder?: Record<string, Record<string, number>>;
  } | null>(null);
  const [stats, setStats] = useState<OrderSourceStats | null>(null);
  const [unknownOrderIds, setUnknownOrderIds] = useState<string[]>([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [tenantMarkets, setTenantMarkets] = useState<(TenantMarket & { market: Market })[]>([]);
  const [marketsLoading, setMarketsLoading] = useState(false);
  const [savingDeadline, setSavingDeadline] = useState<string | null>(null);
  const [deadlineInputs, setDeadlineInputs] = useState<Record<string, string>>({});
  const [tenantSlug, setTenantSlug] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [profilePhoto, setProfilePhoto] = useState<string | undefined>(undefined);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const profilePhotoInputRef = useRef<HTMLInputElement>(null);
  const [description, setDescription] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  // Notification state
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [savingNotifications, setSavingNotifications] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Check access: must be admin or superadmin
  useEffect(() => {
    if (!loading && !isSuperAdmin && !isOwner) {
      toast.error('Accès refusé. Vous devez être administrateur.');
      navigate('/dashboard');
    }
  }, [loading, isSuperAdmin, isOwner, navigate]);

  // Check if merchant notifications are already enabled
  useEffect(() => {
    if (!currentTenantId || !user?.uid) {
      setNotificationsLoading(false);
      return;
    }

    const checkNotificationStatus = async () => {
      setNotificationsLoading(true);
      try {
        const token = await getMerchantNotificationToken(currentTenantId, user.uid);
        setNotificationsEnabled(!!token && hasNotificationPermission());
      } catch (error) {
        console.error('Error checking notification status:', error);
      } finally {
        setNotificationsLoading(false);
      }
    };

    checkNotificationStatus();
  }, [currentTenantId, user?.uid]);

  // Set up foreground message listener when notifications are enabled
  useEffect(() => {
    if (!notificationsEnabled) return;

    const unsubscribe = setupForegroundMessageListener((payload) => {
      console.log('Foreground notification received:', payload);
    });

    return unsubscribe;
  }, [notificationsEnabled]);

  const handleNotificationToggle = async (checked: boolean) => {
    if (!currentTenantId || !user?.uid) {
      toast.error('Aucun tenant sélectionné');
      return;
    }

    setSavingNotifications(true);
    try {
      if (checked) {
        // Enable notifications: request permission and save token
        const result = await requestNotificationPermissionWithDetails();

        if (result.success) {
          await saveMerchantNotificationToken(currentTenantId, user.uid, result.token);
          setNotificationsEnabled(true);
          toast.success('Notifications activées');
        } else {
          // Handle various failure reasons
          switch (result.reason) {
            case 'denied':
              toast.error(
                'Notifications refusées. Veuillez les autoriser dans les paramètres de votre navigateur.'
              );
              break;
            case 'not-supported':
              toast.error(
                'Les notifications ne sont pas supportées par votre navigateur.'
              );
              break;
            case 'ios-purged':
              toast.error(
                'Sur iOS, veuillez supprimer et réinstaller l\'application pour réactiver les notifications.'
              );
              break;
            case 'android-overlay':
              toast.error(
                'Une superposition d\'écran bloque la demande. Désactivez les superpositions et réessayez.'
              );
              break;
            default:
              toast.error('Impossible d\'activer les notifications. Réessayez plus tard.');
          }
        }
      } else {
        // Disable notifications: remove token
        await removeMerchantNotificationToken(currentTenantId, user.uid);
        setNotificationsEnabled(false);
        toast.success('Notifications désactivées');
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      toast.error('Erreur lors de la modification des notifications');
    } finally {
      setSavingNotifications(false);
    }
  };

  // Load tenant and sections
  useEffect(() => {
    if (!currentTenantId && !isSuperAdmin) {
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        let loadedSettings: {
          sectionOrder?: Record<string, number>;
          subSectionOrder?: Record<string, Record<string, number>>;
        } | null = null;

        // Load tenant settings
        if (currentTenantId) {
          const tenant = await getTenantById(currentTenantId);
          if (tenant) {
            loadedSettings = tenant.settings || {};
            setTenantSettings(loadedSettings);
            setIsActive(tenant.isActive);
            setAbsenceStart(tenant.absenceStart || '');
            setAbsenceEnd(tenant.absenceEnd || '');
            setTenantSlug(tenant.slug);
            setTenantName(tenant.name);
            setProfilePhoto(tenant.profilePhoto);
            setDescription(tenant.description || '');
            setPhoneNumber(tenant.phoneNumber || '');
          }
        }

        // Load products to get all sections and subSections
        const products = await listProducts(currentTenantId || undefined);
        const uniqueSections = new Set<string>();
        const subSectionsMap: Record<string, Set<string>> = {};

        products.forEach((product) => {
          const section = product.section || 'Autres';
          uniqueSections.add(section);

          // Collect subSections for each section
          if (product.subSection) {
            if (!subSectionsMap[section]) {
              subSectionsMap[section] = new Set();
            }
            subSectionsMap[section].add(product.subSection);
          }
        });

        const sectionsArray = Array.from(uniqueSections);

        // Apply existing order if available
        if (loadedSettings?.sectionOrder) {
          sectionsArray.sort((a, b) => {
            const orderA = loadedSettings!.sectionOrder![a] ?? 999;
            const orderB = loadedSettings!.sectionOrder![b] ?? 999;
            return orderA - orderB;
          });
        } else {
          sectionsArray.sort();
        }

        setSections(sectionsArray);

        // Convert subSections Sets to sorted arrays
        const subSectionsResult: Record<string, string[]> = {};
        Object.entries(subSectionsMap).forEach(([section, subSectionsSet]) => {
          const subSectionsArray = Array.from(subSectionsSet);

          // Apply existing subSection order if available
          if (loadedSettings?.subSectionOrder?.[section]) {
            subSectionsArray.sort((a, b) => {
              const orderA = loadedSettings!.subSectionOrder![section][a] ?? 999;
              const orderB = loadedSettings!.subSectionOrder![section][b] ?? 999;
              return orderA - orderB;
            });
          } else {
            subSectionsArray.sort();
          }

          subSectionsResult[section] = subSectionsArray;
        });

        setSubSectionsBySection(subSectionsResult);
      } catch (error) {
        console.error('Error loading settings:', error);
        toast.error('Erreur lors du chargement des paramètres');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentTenantId, isSuperAdmin]);

  // Load order source stats
  useEffect(() => {
    if (!currentTenantId || (!isSuperAdmin && !isOwner)) {
      return;
    }

    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const response = await getOrderSourceStats(currentTenantId);
        setStats(response.stats);
        setUnknownOrderIds(response.unknownOrderIds);
      } catch (error) {
        console.error('Error loading order stats:', error);
        // Don't show toast for stats error - it's not critical
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, [currentTenantId, isSuperAdmin, isOwner]);

  // Load tenant markets for deadline configuration
  useEffect(() => {
    if (!currentTenantId || (!isSuperAdmin && !isOwner)) {
      return;
    }

    const loadTenantMarkets = async () => {
      setMarketsLoading(true);
      try {
        const marketsWithDetails = await getTenantMarketsWithDetails(currentTenantId);
        // Sort by day of week
        marketsWithDetails.sort((a, b) => a.market.dayOfWeek - b.market.dayOfWeek);
        setTenantMarkets(marketsWithDetails);
      } catch (error) {
        console.error('Error loading tenant markets:', error);
      } finally {
        setMarketsLoading(false);
      }
    };

    loadTenantMarkets();
  }, [currentTenantId, isSuperAdmin, isOwner]);

  const handleUpdateDeadline = async (
    tenantMarketId: string,
    hours: string
  ) => {
    setSavingDeadline(tenantMarketId);
    try {
      await updateTenantMarket(tenantMarketId, {
        orderDeadlineHours: hours ? parseInt(hours, 10) : undefined,
      });
      // Update local state
      setTenantMarkets((prev) =>
        prev.map((tm) =>
          tm.id === tenantMarketId
            ? { ...tm, orderDeadlineHours: hours ? parseInt(hours, 10) : undefined }
            : tm
        )
      );
      toast.success('Délai de fermeture mis à jour');
    } catch (error) {
      console.error('Error updating deadline:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSavingDeadline(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSubSectionDragEnd = (section: string) => (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setSubSectionsBySection((prev) => {
        const items = prev[section] || [];
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return {
          ...prev,
          [section]: arrayMove(items, oldIndex, newIndex),
        };
      });
    }
  };

  const handleSave = async () => {
    if (!currentTenantId) {
      toast.error('Aucun tenant sélectionné');
      return;
    }

    setSaving(true);
    try {
      // Convert ordered array to Record mapping section name to index
      const sectionOrder: Record<string, number> = {};
      sections.forEach((section, index) => {
        sectionOrder[section] = index;
      });

      // Convert subSections ordered arrays to nested Record
      const subSectionOrder: Record<string, Record<string, number>> = {};
      Object.entries(subSectionsBySection).forEach(([section, subSections]) => {
        subSectionOrder[section] = {};
        subSections.forEach((subSection, index) => {
          subSectionOrder[section][subSection] = index;
        });
      });

      await updateTenantSettings(currentTenantId, {
        sectionOrder,
        subSectionOrder,
      });

      setTenantSettings({ sectionOrder, subSectionOrder });
      toast.success('Paramètres enregistrés avec succès');
    } catch (error) {
      console.error('Error saving settings:', error);
      const err = error as { code?: string; message?: string };

      // Handle specific Firebase errors
      if (err.code === 'permission-denied') {
        toast.error('Permissions insuffisantes. Veuillez vous reconnecter.');
      } else if (err.code === 'unauthenticated') {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else if (err.message?.includes('non authentifié')) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        toast.error(err.message || 'Erreur lors de l\'enregistrement');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleVisibilityChange = async (checked: boolean) => {
    if (!currentTenantId) {
      toast.error('Aucun tenant sélectionné');
      return;
    }

    setSavingVisibility(true);
    try {
      await updateTenantIsActive(currentTenantId, checked);
      setIsActive(checked);
      toast.success(
        checked
          ? 'Votre marché est maintenant visible sur le portail'
          : 'Votre marché est maintenant masqué du portail'
      );
    } catch (error) {
      console.error('Error updating visibility:', error);
      const err = error as { code?: string; message?: string };

      if (err.code === 'permission-denied') {
        toast.error('Permissions insuffisantes. Veuillez vous reconnecter.');
      } else if (err.code === 'unauthenticated') {
        toast.error('Session expirée. Veuillez vous reconnecter.');
      } else {
        toast.error(err.message || 'Erreur lors de la mise à jour');
      }
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleAbsenceSave = async () => {
    if (!currentTenantId) {
      toast.error('Aucun tenant sélectionné');
      return;
    }

    // Validation: if one date is set, both must be set
    if ((absenceStart && !absenceEnd) || (!absenceStart && absenceEnd)) {
      toast.error('Veuillez définir les deux dates de début et fin d\'absence');
      return;
    }

    // Validation: end must be >= start
    if (absenceStart && absenceEnd && absenceEnd < absenceStart) {
      toast.error('La date de fin doit être postérieure à la date de début');
      return;
    }

    setSavingAbsence(true);
    try {
      await updateTenantAbsence(
        currentTenantId,
        absenceStart || null,
        absenceEnd || null
      );
      toast.success(
        absenceStart
          ? 'Période d\'absence enregistrée'
          : 'Période d\'absence supprimée'
      );
    } catch (error) {
      console.error('Error saving absence:', error);
      const err = error as { code?: string; message?: string };
      if (err.code === 'permission-denied') {
        toast.error('Permissions insuffisantes. Veuillez vous reconnecter.');
      } else {
        toast.error(err.message || 'Erreur lors de l\'enregistrement');
      }
    } finally {
      setSavingAbsence(false);
    }
  };

  const handleClearAbsence = async () => {
    setAbsenceStart('');
    setAbsenceEnd('');
    if (currentTenantId) {
      setSavingAbsence(true);
      try {
        await updateTenantAbsence(currentTenantId, null, null);
        toast.success('Période d\'absence supprimée');
      } catch (error) {
        console.error('Error clearing absence:', error);
        toast.error('Erreur lors de la suppression');
      } finally {
        setSavingAbsence(false);
      }
    }
  };

  const handleProfileSave = async () => {
    if (!currentTenantId) {
      toast.error('Aucun tenant sélectionné');
      return;
    }

    setSavingProfile(true);
    try {
      await updateTenantProfile(currentTenantId, {
        description: description.trim() || null,
        phoneNumber: phoneNumber.trim() || null,
      });
      toast.success('Informations mises à jour');
    } catch (error) {
      console.error('Error updating profile:', error);
      const err = error as { code?: string; message?: string };
      if (err.code === 'permission-denied') {
        toast.error('Permissions insuffisantes. Veuillez vous reconnecter.');
      } else {
        toast.error(err.message || 'Erreur lors de la mise à jour');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handleProfilePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentTenantId) return;

    if (!isValidImageFormat(file)) {
      toast.error('Format non supporté (JPEG, PNG, WebP, HEIC)');
      e.target.value = '';
      return;
    }

    setSavingPhoto(true);
    try {
      const url = await uploadMerchantProfilePhoto(currentTenantId, file);
      setProfilePhoto(url);
      toast.success('Photo de profil mise à jour');
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error("Erreur lors de l'upload de la photo");
    } finally {
      setSavingPhoto(false);
      e.target.value = '';
    }
  };

  const handleDeleteProfilePhoto = async () => {
    if (!currentTenantId) return;
    setSavingPhoto(true);
    try {
      await deleteMerchantProfilePhoto(currentTenantId);
      setProfilePhoto(undefined);
      toast.success('Photo de profil supprimée');
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setSavingPhoto(false);
    }
  };

  // Check if currently absent
  const isCurrentlyAbsent = isTenantAbsent({ absenceStart, absenceEnd });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground">Chargement...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!isSuperAdmin && !isOwner) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8 pb-24">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
          <h1 className="text-3xl font-bold">Paramètres du tenant</h1>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Photo de profil
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Visible par les clients sur la page du marché pour vous reconnaître facilement.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt="Photo de profil"
                    className="h-20 w-20 rounded-full object-cover border"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center border border-orange-200">
                    <Camera className="h-8 w-8 text-orange-400" />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={savingPhoto || !currentTenantId}
                  onClick={() => profilePhotoInputRef.current?.click()}
                >
                  <Camera className="h-4 w-4" />
                  {savingPhoto ? 'Upload en cours...' : profilePhoto ? 'Modifier la photo' : 'Ajouter une photo'}
                </Button>
                <input
                  ref={profilePhotoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  className="sr-only"
                  onChange={handleProfilePhotoChange}
                />
                {profilePhoto && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 text-destructive hover:text-destructive"
                    onClick={handleDeleteProfilePhoto}
                    disabled={savingPhoto}
                  >
                    <X className="h-4 w-4" />
                    Supprimer la photo
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">JPEG, PNG, WebP ou HEIC · max 500 Ko</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Informations du profil
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Visibles par les clients sur votre page de commande et le portail marché.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Présentez votre activité, vos spécialités, votre terroir..."
                rows={3}
                disabled={savingProfile || !currentTenantId}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phoneNumber" className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5" />
                Numéro de téléphone
              </Label>
              <Input
                id="phoneNumber"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="06 12 34 56 78"
                disabled={savingProfile || !currentTenantId}
              />
            </div>
            <Button
              onClick={handleProfileSave}
              disabled={savingProfile || !currentTenantId}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {savingProfile ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Visibilité sur le portail
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Contrôlez si votre marché apparaît dans la liste des marchés sur le portail client.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="font-medium">Référencement actif</p>
                <p className="text-sm text-muted-foreground">
                  {isActive
                    ? 'Votre marché est visible et les clients peuvent passer commande.'
                    : 'Votre marché est masqué du portail. Les clients avec le lien direct peuvent toujours commander.'}
                </p>
              </div>
              <Switch
                checked={isActive}
                onCheckedChange={handleVisibilityChange}
                disabled={savingVisibility || !currentTenantId}
              />
            </div>
          </CardContent>
        </Card>

        {tenantSlug && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="h-5 w-5" />
                QR Code de commande
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Partagez ce QR code pour que vos clients accèdent directement à votre page de commande.
              </p>
            </CardHeader>
            <CardContent>
              <QRCodeSection slug={tenantSlug} tenantName={tenantName} />
            </CardContent>
          </Card>
        )}

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {notificationsEnabled ? (
                <Bell className="h-5 w-5" />
              ) : (
                <BellOff className="h-5 w-5" />
              )}
              Notifications de commandes
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Recevez une notification push sur cet appareil quand un client passe une commande.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="font-medium">Notifications push</p>
                <p className="text-sm text-muted-foreground">
                  {notificationsLoading
                    ? 'Vérification...'
                    : notificationsEnabled
                      ? 'Vous serez notifié à chaque nouvelle commande client.'
                      : isNotificationPermissionDenied()
                        ? 'Les notifications sont bloquées par votre navigateur. Modifiez les paramètres de votre navigateur pour les autoriser.'
                        : 'Activez pour recevoir une alerte à chaque commande.'}
                </p>
              </div>
              <Switch
                checked={notificationsEnabled}
                onCheckedChange={handleNotificationToggle}
                disabled={savingNotifications || notificationsLoading || !currentTenantId}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Période d'absence
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Définissez une période pendant laquelle vous ne serez pas disponible.
              Votre marché sera masqué du portail et les clients verront un message d'absence.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="absenceStart">Date de début</Label>
                <Input
                  id="absenceStart"
                  type="date"
                  value={absenceStart}
                  onChange={(e) => setAbsenceStart(e.target.value)}
                  disabled={savingAbsence}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="absenceEnd">Date de fin</Label>
                <Input
                  id="absenceEnd"
                  type="date"
                  value={absenceEnd}
                  onChange={(e) => setAbsenceEnd(e.target.value)}
                  disabled={savingAbsence}
                />
              </div>
            </div>

            {absenceStart && absenceEnd && (
              <div className={cn(
                'p-3 border rounded-lg',
                isCurrentlyAbsent
                  ? 'bg-red-50 border-red-200'
                  : 'bg-orange-50 border-orange-200'
              )}>
                <p className={cn(
                  'text-sm',
                  isCurrentlyAbsent ? 'text-red-800' : 'text-orange-800'
                )}>
                  <AlertCircle className="inline-block h-4 w-4 mr-1" />
                  {isCurrentlyAbsent
                    ? 'Vous êtes actuellement marqué comme absent.'
                    : `Vous serez marqué comme absent du ${new Date(absenceStart + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} au ${new Date(absenceEnd + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}.`
                  }
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={handleAbsenceSave}
                disabled={savingAbsence || !currentTenantId}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {savingAbsence ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
              {(absenceStart || absenceEnd) && (
                <Button
                  variant="outline"
                  onClick={handleClearAbsence}
                  disabled={savingAbsence}
                >
                  Supprimer l'absence
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Timer className="h-5 w-5" />
              Fermeture anticipée des commandes
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Définissez un délai avant l'ouverture du marché pour fermer automatiquement les commandes.
              Par exemple, 24h signifie que les commandes ferment la veille à l'heure d'ouverture du marché.
            </p>
          </CardHeader>
          <CardContent>
            {marketsLoading ? (
              <p className="text-center text-muted-foreground py-4">Chargement...</p>
            ) : tenantMarkets.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Aucun marché associé à votre compte.
              </p>
            ) : (
              <div className="space-y-4">
                {tenantMarkets.map((tm) => (
                  <div
                    key={tm.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="font-medium">
                        {tm.market.city} — {DAY_OF_WEEK_LABELS[tm.market.dayOfWeek]}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                        <Clock className="h-3 w-3" />
                        {tm.market.openingTime} – {tm.market.closingTime}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`deadline-${tm.id}`} className="text-sm whitespace-nowrap">
                        Fermer
                      </Label>
                      <Input
                        id={`deadline-${tm.id}`}
                        type="number"
                        min="0"
                        step="1"
                        className="w-20"
                        value={deadlineInputs[tm.id] ?? (tm.orderDeadlineHours != null ? String(tm.orderDeadlineHours) : '')}
                        onChange={(e) => setDeadlineInputs(prev => ({ ...prev, [tm.id]: e.target.value }))}
                        onBlur={(e) => {
                          const val = e.target.value;
                          const current = tm.orderDeadlineHours != null ? String(tm.orderDeadlineHours) : '';
                          if (val !== current) {
                            handleUpdateDeadline(tm.id, val);
                          }
                          setDeadlineInputs(prev => { const next = { ...prev }; delete next[tm.id]; return next; });
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            (e.target as HTMLInputElement).blur();
                          }
                        }}
                        disabled={savingDeadline === tm.id}
                        placeholder="—"
                      />
                      <span className="text-sm text-muted-foreground whitespace-nowrap">h avant</span>
                    </div>
                    {tm.orderDeadlineHours && (
                      <p className="text-xs text-muted-foreground sm:ml-2">
                        Les commandes ferment {tm.orderDeadlineHours}h avant l'ouverture ({tm.market.openingTime})
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Statistiques des commandes
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Répartition des commandes par source (client ou commerçant).
            </p>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <p className="text-center text-muted-foreground py-4">Chargement...</p>
            ) : stats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-700">{stats.customer}</p>
                    <p className="text-sm text-blue-600">Clients</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">{stats.merchant}</p>
                    <p className="text-sm text-green-600">Commerçant</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
                    <p className="text-sm text-gray-600">Total</p>
                  </div>
                </div>
                {stats.unknown > 0 && (
                  <div className="text-xs text-muted-foreground text-center space-y-1">
                    <p>
                      {stats.unknown} commande{stats.unknown > 1 ? 's' : ''} antérieure{stats.unknown > 1 ? 's' : ''} sans source enregistrée
                    </p>
                    {unknownOrderIds.length > 0 && (
                      <p className="font-mono text-[10px] bg-gray-100 p-2 rounded">
                        ID{unknownOrderIds.length > 1 ? 's' : ''}: {unknownOrderIds.join(', ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-4">
                Impossible de charger les statistiques
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Ordre des sections</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Glissez-déposez les sections pour définir leur ordre d'affichage sur la page de commande et dans la gestion des produits.
            </p>
          </CardHeader>
          <CardContent>
            {sections.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Aucune section trouvée. Créez des produits avec des sections pour les voir apparaître ici.
              </p>
            ) : (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sections}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {sections.map((section) => (
                        <SortableSectionItem
                          key={section}
                          section={section}
                          index={sections.indexOf(section)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

              </>
            )}
          </CardContent>
        </Card>

        {/* SubSections ordering card - only shown if there are subSections */}
        {Object.keys(subSectionsBySection).length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Ordre des sous-sections</CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Organisez l'ordre des sous-sections pour chaque section. Les sous-sections apparaissent dans la barre de navigation secondaire.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {sections
                .filter((section) => subSectionsBySection[section]?.length > 0)
                .map((section) => (
                  <div key={section} className="space-y-3">
                    <h4 className="font-medium text-gray-700 border-b pb-2">
                      {capitalize(section)}
                    </h4>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleSubSectionDragEnd(section)}
                    >
                      <SortableContext
                        items={subSectionsBySection[section]}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2 pl-4">
                          {subSectionsBySection[section].map((subSection) => (
                            <SortableSubSectionItem
                              key={subSection}
                              subSection={subSection}
                              index={subSectionsBySection[section].indexOf(subSection)}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                ))}
            </CardContent>
          </Card>
        )}

        {/* Save button */}
        {(sections.length > 0 || Object.keys(subSectionsBySection).length > 0) && (
          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving || !currentTenantId}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
